'use client';

import { useCallback, useEffect, useState } from 'react';

import { useCatalogPage } from './use-catalog-page';
import { fetchWithAppAuth } from '@/lib/auth/client-api';
import { getFirestoreClient } from '@/lib/firebase/client';
import { uploadReaderFile } from '@/lib/reader/upload-client';
import {
  getPublicTitleBySlug,
  getTitleBySourceProjectId,
  listPublicTitles,
  listTitlesForAuthor,
  listTitlesForOwner,
} from '@/lib/platform/titles';
import type { TitleCommerceAssetDoc, TitleCommerceAssetKind, TitleDoc } from '@/types/platform';

type AdminTitleApiResponse = {
  ok?: boolean;
  title?: TitleDoc;
  titles?: TitleDoc[];
  total?: number;
  nextCursor?: string | null;
  error?: string;
  asset?: TitleCommerceAssetDoc;
  assets?: TitleCommerceAssetDoc[];
  assetId?: string;
  uploadUrl?: string;
  contentType?: string;
  readiness?: {
    ready: boolean;
    blocking: string[];
    warnings: string[];
  };
};

async function readAdminTitleResponse(response: Response, fallbackMessage: string) {
  const json = await response.json().catch(() => ({})) as AdminTitleApiResponse;
  if (!response.ok) {
    throw new Error(json.error || fallbackMessage);
  }
  return json;
}

export function usePublicTitles(initialTitles?: TitleDoc[]) {
  const [titles, setTitles] = useState<TitleDoc[]>(initialTitles ?? []);
  const [loading, setLoading] = useState(initialTitles === undefined);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const db = getFirestoreClient();
    if (!db) {
      setTitles([]);
      setLoading(false);
      setError('Firebase client environment is not configured yet.');
      return;
    }
    try {
      setLoading(true);
      const data = await listPublicTitles(db);
      setTitles(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load published titles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialTitles !== undefined) return undefined;
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [initialTitles, refresh]);

  return { titles, loading, error, refresh };
}

export function useAdminTitles(pageSize = 100) {
  const { records: titles, setRecords, ...page } = useCatalogPage<TitleDoc>(`/api/admin/titles?limit=${pageSize}`, 'titles', 'titleId');
  const updateTitle = useCallback((title: TitleDoc) => {
    setRecords(current => current.map(item => item.titleId === title.titleId ? title : item));
  }, [setRecords]);
  return { titles, ...page, updateTitle };
}

export function usePublicTitle(titleSlug: string, initialTitle: TitleDoc | null = null, previewMode = false) {
  const [title, setTitle] = useState<TitleDoc | null>(initialTitle);
  const [loading, setLoading] = useState(!initialTitle);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (previewMode) return;
    const db = getFirestoreClient();
    if (!db) {
      setTitle(null);
      setLoading(false);
      setError('Firebase client environment is not configured yet.');
      return;
    }
    try {
      setLoading(true);
      const data = await getPublicTitleBySlug(db, titleSlug);
      setTitle(data && data.storefrontVisibility && ['approved', 'published'].includes(data.publicationStatus) ? data : null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load title.');
    } finally {
      setLoading(false);
    }
  }, [titleSlug, previewMode]);

  useEffect(() => {
    if (previewMode) return;
    if (initialTitle?.slug === titleSlug) return undefined;
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [initialTitle, refresh, titleSlug, previewMode]);

  return { title, loading, error, refresh };
}

export function useTitlesForAuthor(authorProfileId?: string | null) {
  const [titles, setTitles] = useState<TitleDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!authorProfileId) {
      setTitles([]);
      setLoading(false);
      return;
    }
    const db = getFirestoreClient();
    if (!db) {
      setTitles([]);
      setLoading(false);
      setError('Firebase client environment is not configured yet.');
      return;
    }
    try {
      setLoading(true);
      const data = await listTitlesForAuthor(db, authorProfileId);
      setTitles(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load author titles.');
    } finally {
      setLoading(false);
    }
  }, [authorProfileId]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [refresh]);

  return { titles, loading, error, refresh };
}

export function useTitlesForOwner(ownerUid?: string | null) {
  const [titles, setTitles] = useState<TitleDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!ownerUid) {
      setTitles([]);
      setLoading(false);
      return;
    }
    const db = getFirestoreClient();
    if (!db) {
      setTitles([]);
      setLoading(false);
      setError('Firebase client environment is not configured yet.');
      return;
    }
    try {
      setLoading(true);
      const data = await listTitlesForOwner(db, ownerUid);
      setTitles(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load owner titles.');
    } finally {
      setLoading(false);
    }
  }, [ownerUid]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [refresh]);

  return { titles, loading, error, refresh };
}

export function useProjectTitle(projectId?: string | null) {
  const [title, setTitle] = useState<TitleDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!projectId) {
      setTitle(null);
      setLoading(false);
      return;
    }
    const db = getFirestoreClient();
    if (!db) {
      setTitle(null);
      setLoading(false);
      setError('Firebase client environment is not configured yet.');
      return;
    }
    try {
      setLoading(true);
      const data = await getTitleBySourceProjectId(db, projectId);
      setTitle(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load project title bridge.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [refresh]);

  return { title, loading, error, refresh };
}

export async function updatePublishedTitle(titleId: string, patch: Partial<TitleDoc> & { expectedSessionUid?: string; expectedRevision?: string; expectedCoverAlt?: string; expectedSampleAssetId?: string; expectedAssetRevision?: string; publicationAction?: string }) {
  const response = await fetchWithAppAuth(`/api/admin/titles/${encodeURIComponent(titleId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const json = await readAdminTitleResponse(response, 'Unable to update title.');
  if (!json.ok || !json.title) throw new Error('Save was not confirmed. Reload the saved title before retrying.');
  return json.title;
}

export async function createAdminTitle(input: Partial<TitleDoc> & { expectedSessionUid?: string }) {
  const response = await fetchWithAppAuth('/api/admin/titles', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  }).catch(() => { throw new Error('Save was not confirmed. The request may have completed. Your edits are preserved; check saved state before retrying.'); });
  const json = await readAdminTitleResponse(response, 'Unable to create title.');
  if (!json.ok || !json.title?.titleId) throw new Error('Creation was not confirmed. Check saved titles before retrying.');
  return json.title;
}

export async function uploadTitleAsset(input: {
  expectedSessionUid: string;
  assertSession: () => void;
  file: File;
  kind: TitleCommerceAssetKind;
  titleId?: string;
  signal?: AbortSignal;
  onProgress?: (percent: number) => void;
}) {
  if (!input.titleId) throw new Error('Save the title draft before uploading its files.');
  input.assertSession();
  const endpoint = `/api/admin/titles/${encodeURIComponent(input.titleId)}/assets`;
  const response = await fetchWithAppAuth(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expectedSessionUid: input.expectedSessionUid, kind: input.kind, fileName: input.file.name, fileSize: input.file.size }),
  });
  const registration = await readAdminTitleResponse(response, 'Unable to register title asset upload.');
  if (!registration.assetId || !registration.uploadUrl || !registration.contentType) throw new Error('The title asset upload session is incomplete.');
  let json: AdminTitleApiResponse;
  try {
    input.assertSession();
    const signal = input.signal ?? new AbortController().signal;
    const generation = await uploadReaderFile(input.file, registration.uploadUrl, signal, input.onProgress ?? (() => undefined), registration.contentType);
    input.assertSession();
    const completed = await fetchWithAppAuth(`${endpoint}/${encodeURIComponent(registration.assetId)}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ generation, expectedSessionUid: input.expectedSessionUid }),
      signal,
    });
    json = await readAdminTitleResponse(completed, 'Unable to complete title asset upload.');
    input.assertSession();
    if (!json.ok || !json.asset) throw new Error('Upload completion was not confirmed. Check the saved file before retrying.');
  } catch (error) {
    // Never complete, cancel or replay using a different account, even after an abort.
    input.assertSession();
    // Preserve a completed upload after transient completion/format errors so the
    // operator can retry it. Only an explicit stop cancels the registration.
    if (input.signal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
      await fetchWithAppAuth(`${endpoint}/${encodeURIComponent(registration.assetId)}/complete`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectedSessionUid: input.expectedSessionUid }) }).catch(() => undefined);
    }
    throw error;
  }
  return {
    asset: json.asset,
    title: json.title,
    readiness: json.readiness,
  };
}

export async function listTitleAssets(titleId: string) {
  const response = await fetchWithAppAuth(`/api/admin/titles/${encodeURIComponent(titleId)}/assets`, { cache: 'no-store' });
  const json = await readAdminTitleResponse(response, 'Unable to load title assets.');
  return json.assets ?? [];
}
